import React from 'react';
import PropTypes from 'prop-types';
import { upperFirst } from 'lodash';
import { formatMoney, formatNumber, pluralize } from '@lib/utils';

// ---------------------------------------------------------

import { market_summary, heading, summary_text } from './styles.module.scss';

// ---------------------------------------------------------

/**
 * Concise, crawlable market summary for search result pages. Renders static,
 * server-provided aggregate data (counts + price range) so answer engines can
 * extract a direct overview of the market for a location. Each sentence is
 * guarded so a missing metric never renders a broken line.
 */
const MarketSummary = ({
  locationName,
  statusLabel,
  totalListings,
  activeCount,
  pendingCount,
  minPrice,
  maxPrice,
  medianPrice,
  isSampled
}) => {
  if (!totalListings) return null;

  // A single searched location gets its bare name and a singular verb ("Covington,
  // KY has …"); multiple locations (no single label) read "Search Results have …".
  const isSingleLocation = Boolean(locationName);
  const place = locationName || 'Search Results';
  const verb = isSingleLocation ? 'has' : 'have';

  // Qualifies the copy for non-current searches, so a status=Sold page reads
  // "…has 1,234 sold listings" rather than implying they're available now.
  const qualifier = statusLabel ? `${statusLabel} ` : '';

  // For sampled result sets (more listings than we can fetch in one page) the
  // status counts and price range would be partial, so only the exact total is
  // shown to avoid presenting inaccurate values. Zero counts are also skipped —
  // a Sold-filtered search has no active/pending listings, so stating "0 active
  // and 0 pending" would be true but pointless noise in the sentence.
  const statusParts = [];
  if (!isSampled && activeCount > 0) statusParts.push(`${activeCount} active`);
  if (!isSampled && pendingCount > 0) statusParts.push(`${pendingCount} pending`);
  const countSentence = `${place} ${verb} ${formatNumber(totalListings)} ${qualifier}${pluralize(
    'listing',
    totalListings
  )}${statusParts.length ? `, of which there are ${statusParts.join(' and ')}` : ''}.`;

  const hasRange = !isSampled && minPrice != null && maxPrice != null;
  let priceSentence = '';
  if (hasRange) {
    const medianText =
      !isSampled && medianPrice != null ? `, with a median of ${formatMoney(medianPrice)}` : '';
    const priceSubject = statusLabel ? `${upperFirst(statusLabel)} prices` : 'Prices';
    priceSentence = `${priceSubject} range from ${formatMoney(minPrice)} to ${formatMoney(
      maxPrice
    )}${medianText}.`;
  }

  return (
    <section className={market_summary} aria-label={`${place} market summary`}>
      <h2 className={heading}>{place} Market Summary</h2>
      <p className={summary_text}>{countSentence}</p>
      {priceSentence && <p className={summary_text}>{priceSentence}</p>}
    </section>
  );
};

MarketSummary.propTypes = {
  locationName: PropTypes.string,

  /**
   * Qualifier for non-current searches, e.g. "sold".
   */
  statusLabel: PropTypes.string,

  totalListings: PropTypes.number,
  activeCount: PropTypes.number,
  pendingCount: PropTypes.number,
  minPrice: PropTypes.number,
  maxPrice: PropTypes.number,
  medianPrice: PropTypes.number,
  isSampled: PropTypes.bool
};

export default MarketSummary;
